import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateFinanceData } from '../../utils/financeQueries';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import NoteAddIcon from '@mui/icons-material/NoteAddOutlined';
import { useAuth } from '../../context/authContext';
import {
  approvePayment,
  createPayment,
  downloadInvoice,
  downloadReceipt,
  downloadReceiptDocument,
  getInvoiceBlob,
  getReceiptBlob,
  getReceiptDocumentBlob,
  getStudentLedger,
  getStudentPaymentSummary,
  listPayments,
  rejectPayment,
  resubmitPayment,
  uploadPaymentSlip,
} from '../../api/payments';
import { listLookup } from '../../api/masterData';
import { getFinanceLookups } from '../../api/finance';
import { Payment, PaymentMethod, PaymentType, ReceiptDocument } from '../../types/payment';
import { ExchangeRate } from '../../types/exchangeRate';
import { ExchangeRateSelect } from '../../components/ExchangeRateSelect';
import { GenerateReceiptDialog } from './GenerateReceiptDialog';
import { ReceiptFilePicker } from '../../components/payments/ReceiptFilePicker';
import { PaymentEvidenceList } from '../../components/payments/PaymentEvidenceList';
import { RequestReceiptDialog } from '../../components/payments/RequestReceiptDialog';
import { RECEIPT_ACCEPT, receiptFileProblem } from '../../utils/paymentReceipts';
import { formatMoney, formatRate, formatRateDateTime, rateOriginLabel } from '../../utils/exchangeRates';
import { ApprovalStatusChip } from '../finance/ApprovalStatusChip';
import { PaymentChecklistCard } from './PaymentChecklistCard';
import { StudentFeeBreakdownCard } from './StudentFeeBreakdownCard';
import { StudentLedgerDialog } from './StudentLedgerDialog';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';
import { NumberLabel } from '../../components/NumberLabel';

const errorMessage = (err: unknown, fallback: string) =>
  isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

const TYPE_COLOR: Record<PaymentType, 'primary' | 'info' | 'success' | 'warning'> = {
  PAYMENT: 'primary',
  DISCOUNT: 'info',
  SCHOLARSHIP: 'success',
  REFUND: 'warning',
};

const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'CARD', 'ONLINE_PAYMENT'];
const PAYMENT_TYPES: PaymentType[] = ['PAYMENT', 'DISCOUNT', 'SCHOLARSHIP', 'REFUND'];

export const StudentPaymentsTab = ({
  studentId,
  highlightPaymentId,
  lookupSource = 'masterData',
}: {
  studentId: number;
  /** From a payment notification link (`?paymentId=<id>`). */
  highlightPaymentId?: number | null;
  /** Where the Record-payment dropdowns come from. The Finance workspace
   * (Phase 96) uses /finance/lookups, since Finance Admin holds no
   * master_data:view. */
  lookupSource?: 'masterData' | 'finance';
}) => {
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  /** Phase 97 — the rejected payment being corrected and resubmitted (the
   * record dialog is reused, prefilled). */
  const [resubmitting, setResubmitting] = useState<Payment | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Payment | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [type, setType] = useState<PaymentType>('PAYMENT');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [currencyId, setCurrencyId] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  /** Phase 104 — a rate other than the current automatic one (finance only). */
  const [paymentRate, setPaymentRate] = useState<ExchangeRate | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<Payment | null>(null);
  // Phase 107 — the receipt attached while recording, and its upload.
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [requestTarget, setRequestTarget] = useState<Payment | null>(null);

  const paymentsQuery = useQuery({
    queryKey: ['student-payments', studentId],
    queryFn: () => listPayments({ page: 1, pageSize: 100, studentId }),
  });

  // Scroll a notification's payment into view once the list has loaded.
  useEffect(() => {
    if (!highlightPaymentId || !paymentsQuery.data) return;
    document.getElementById(`payment-${highlightPaymentId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [highlightPaymentId, paymentsQuery.data]);
  const summaryQuery = useQuery({
    queryKey: ['payment-summary', studentId],
    queryFn: () => getStudentPaymentSummary(studentId),
  });
  const ledgerSummaryQuery = useQuery({
    queryKey: ['student-ledger-summary', studentId],
    queryFn: () => getStudentLedger(studentId),
  });
  const categoriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/payment-categories'],
    queryFn: () => listLookup('/master-data/payment-categories', { activeOnly: true }),
    enabled: createOpen && lookupSource === 'masterData',
  });
  const currenciesQuery = useQuery({
    queryKey: ['lookup', '/master-data/currencies'],
    queryFn: () => listLookup('/master-data/currencies', { activeOnly: true }),
    enabled: createOpen && lookupSource === 'masterData',
  });
  const financeLookupsQuery = useQuery({
    queryKey: ['finance-lookups'],
    queryFn: getFinanceLookups,
    enabled: createOpen && lookupSource === 'finance',
  });
  const categoryOptions: Array<{ id: number; name: string; isTaxable?: boolean }> =
    lookupSource === 'finance'
      ? (financeLookupsQuery.data?.paymentCategories ?? [])
      : (categoriesQuery.data ?? []).map((c) => ({
          id: c.id as number,
          name: String(c.name),
          isTaxable: c.isTaxable === true,
        }));
  const currencyOptions: Array<{ id: number; code: string }> =
    lookupSource === 'finance'
      ? (financeLookupsQuery.data?.currencies ?? [])
      : (currenciesQuery.data ?? []).map((c) => ({ id: c.id as number, code: String(c.code) }));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['student-payments', studentId] });
    queryClient.invalidateQueries({ queryKey: ['payment-summary', studentId] });
    queryClient.invalidateQueries({ queryKey: ['student-ledger-summary', studentId] });
    queryClient.invalidateQueries({ queryKey: ['student-ledger', studentId] });
    // Payment Checklist card (Payment/Document Checklist correction) —
    // every mutation below changes a payment's status/amount, so its
    // per-category totals need the same refresh the rest of this tab gets.
    queryClient.invalidateQueries({ queryKey: ['payment-checklist', studentId] });
    // Finance workspace (Phase 96) — its balances, lists and audit history —
    // and the review counts (Phase 102).
    invalidateFinanceData(queryClient);
  };

  const resetForm = () => {
    setCreateOpen(false);
    setResubmitting(null);
    setType('PAYMENT');
    setCategoryId('');
    setAmount('');
    setCurrencyId('');
    setPaymentMethod('');
    setReferenceNumber('');
    setPaymentRate(null);
    setReceiptFile(null);
    setReceiptError(null);
    setUploadProgress(null);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const fields = {
        paymentCategoryId: categoryId as number,
        amount: Number(amount),
        currencyId: currencyId as number,
        paymentMethod: paymentMethod || undefined,
        referenceNumber: referenceNumber || undefined,
        ...(paymentRate ? { exchangeRateId: paymentRate.id } : {}),
      };
      const upload = receiptFile ? { receipt: receiptFile, onProgress: setUploadProgress } : {};
      if (receiptFile) setUploadProgress(0);
      return resubmitting
        ? resubmitPayment(resubmitting.id, fields, upload)
        : createPayment({ studentId, type, ...fields }, upload);
    },
    onSuccess: () => {
      invalidate();
      resetForm();
    },
    onError: () => setUploadProgress(null),
  });

  const openResubmit = (p: Payment) => {
    createMutation.reset();
    setResubmitting(p);
    setType(p.type);
    setCategoryId(p.category.id);
    setAmount(String(Number(p.amount)));
    setCurrencyId(p.currency.id);
    setPaymentMethod(p.paymentMethod ?? '');
    setReferenceNumber(p.referenceNumber ?? '');
    setCreateOpen(true);
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => approvePayment(id),
    onSuccess: invalidate,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectPayment(id, reason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectReason('');
    },
  });

  const slipUploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => {
      const problem = receiptFileProblem(file);
      if (problem) return Promise.reject(new Error(problem));
      return uploadPaymentSlip(id, file);
    },
    onSuccess: invalidate,
  });

  const canApprove = hasPermission('payments', 'approve');
  // Phase 107 — a finance reviewer sees a pending payment's documents; for
  // everyone else (a Counselor) they wait for Finance approval. The API
  // enforces the same.
  const documentsAvailable = (p: Payment) => p.status === 'APPROVED' || canApprove;
  const canViewReceipts = hasPermission('receipts', 'view');
  const canUploadSlip = hasPermission('payments', 'create');
  const canResubmit = hasPermission('payments', 'create');
  // Phase 104 — Generate Receipt (currency + rate), and choosing a manual or
  // historical rate (Super Admin / finance only; the server enforces both).
  const canGenerateReceipt = hasPermission('receipts', 'create');
  const canChooseRate = hasPermission('exchange_rates', 'manage');

  const previewReceiptDocument = (p: Payment, d: ReceiptDocument) => {
    const fileName = `${p.receipt!.receiptNumber}-${d.currency}${d.version > 1 ? `-v${d.version}` : ''}.pdf`;
    setPreviewTarget({
      key: `receipt-document-${d.id}`,
      originalName: fileName,
      mimeType: 'application/pdf',
      fetchBlob: () => getReceiptDocumentBlob(p.id, d.id),
      onDownload: () => downloadReceiptDocument(p.id, d.id, fileName),
    });
  };
  // Maker-checker (Phase 97) — the server refuses a reviewer's own
  // submission; this only hides the buttons it would refuse.
  const canReview = (p: Payment) =>
    canApprove && (p.recordedBy.id !== user?.id || user?.role.name === 'Super Admin');

  const slipInputRef = useRef<HTMLInputElement>(null);
  const [slipUploadTargetId, setSlipUploadTargetId] = useState<number | null>(null);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  return (
    <Box sx={{ mt: 2 }}>
      <PaymentChecklistCard studentId={studentId} />
      <StudentFeeBreakdownCard studentId={studentId} />

      <input
        ref={slipInputRef}
        type="file"
        accept={RECEIPT_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && slipUploadTargetId) slipUploadMutation.mutate({ id: slipUploadTargetId, file });
          e.target.value = '';
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {summaryQuery.data?.map((s) => (
            <Chip key={s.currencyId} label={`Total paid: ${s.total.toFixed(2)}`} color="primary" variant="outlined" />
          ))}
          {ledgerSummaryQuery.data?.summary.map((s) => (
            <Chip
              key={s.currency.id}
              label={`Outstanding: ${s.currency.symbol ?? s.currency.code} ${s.outstandingBalance.toFixed(2)}`}
              color={s.outstandingBalance > 0 ? 'warning' : 'success'}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<AccountBalanceIcon />} onClick={() => setLedgerOpen(true)}>
            Ledger
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              createMutation.reset();
              setCreateOpen(true);
            }}
          >
            Record payment
          </Button>
        </Stack>
      </Stack>

      {slipUploadMutation.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => slipUploadMutation.reset()}>
          {slipUploadMutation.error instanceof Error && !('isAxiosError' in slipUploadMutation.error)
            ? slipUploadMutation.error.message
            : errorMessage(slipUploadMutation.error, "Couldn't upload the receipt")}
        </Alert>
      )}
      {approveMutation.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => approveMutation.reset()}>
          {errorMessage(approveMutation.error, "Couldn't approve the payment")}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {paymentsQuery.data?.data.map((p) => (
          <Paper
            key={p.id}
            id={`payment-${p.id}`}
            variant="outlined"
            sx={{
              p: 2,
              ...(p.id === highlightPaymentId ? { borderColor: 'primary.main', borderWidth: 2 } : {}),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle2">
                  <NumberLabel value={p.paymentNumber} kind="payment" previous={p.temporaryPaymentNumber} variant="inherit" /> — {p.category.name} — {p.currency.symbol ?? p.currency.code} {Number(p.amount).toFixed(2)}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip size="small" label={p.type} color={TYPE_COLOR[p.type]} />
                  <ApprovalStatusChip status={p.status} />
                  {p.paymentMethod && <Chip size="small" label={p.paymentMethod.replace(/_/g, ' ')} variant="outlined" />}
                  {p.referenceNumber && <Chip size="small" label={`Ref: ${p.referenceNumber}`} variant="outlined" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  Recorded by {p.recordedBy.firstName} {p.recordedBy.lastName} on{' '}
                  {new Date(p.createdAt).toLocaleDateString()}
                </Typography>
                <PaymentEvidenceList
                  paymentId={p.id}
                  evidence={(p.evidence ?? []).map((e) => ({
                    ...e,
                    uploadedBy: e.uploadedBy ? `${e.uploadedBy.firstName} ${e.uploadedBy.lastName}` : null,
                  }))}
                  onPreview={setPreviewTarget}
                />
                {p.status === 'PENDING' && p.evidenceRequestedAt && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    Finance requested {p.evidenceRequestKind === 'ADDITIONAL' ? 'an additional' : 'a replacement'} receipt
                    {p.evidenceRequestedBy ? ` (${p.evidenceRequestedBy.firstName} ${p.evidenceRequestedBy.lastName})` : ''}:{' '}
                    {p.evidenceRequestReason}
                  </Alert>
                )}
                {p.exchangeRate && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Exchange rate: Rs. {formatRate(p.exchangeRate)} ({rateOriginLabel(p.usedExchangeRate?.origin)}
                    {p.usedExchangeRate ? `, effective ${formatRateDateTime(p.usedExchangeRate.sourcePublishedAt)}` : ''})
                    {p.lkrAmount ? ` · LKR ${formatMoney(Number(p.lkrAmount))}` : ''}
                  </Typography>
                )}
                {p.receipt?.documents?.map((d) => (
                  <Stack
                    key={d.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    data-testid="receipt-document"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Receipt {d.currency} v{d.version}
                      {d.exchangeRate ? ` · Rs. ${formatRate(d.exchangeRate)} (${rateOriginLabel(d.exchangeRateOrigin)})` : ''}
                      {' · '}
                      {d.currency === 'USD' && d.amountUsd ? `USD ${formatMoney(Number(d.amountUsd))}` : ''}
                      {d.currency === 'LKR' && d.amountLkr ? `LKR ${formatMoney(Number(d.amountLkr))}` : ''}
                      {` · ${d.generatedBy.firstName} ${d.generatedBy.lastName}, ${new Date(d.generatedAt).toLocaleDateString()}`}
                    </Typography>
                    <Button size="small" sx={{ minWidth: 0, py: 0 }} onClick={() => previewReceiptDocument(p, d)}>
                      View
                    </Button>
                  </Stack>
                ))}
                {p.status !== 'PENDING' && p.approvedBy && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {p.status === 'APPROVED' ? 'Approved' : 'Rejected'} by {p.approvedBy.firstName}{' '}
                    {p.approvedBy.lastName}
                    {p.approvedAt ? ` on ${new Date(p.approvedAt).toLocaleDateString()}` : ''}
                  </Typography>
                )}
                {p.resubmittedFrom && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Corrected resubmission of {p.resubmittedFrom.paymentNumber}
                  </Typography>
                )}
                {p.status === 'REJECTED' && (
                  <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                    Rejection reason: {p.rejectionReason ?? 'not recorded'}
                    {p.resubmission
                      ? ` — resubmitted as ${p.resubmission.paymentNumber}.`
                      : ''}
                  </Alert>
                )}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                {!documentsAvailable(p) && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }} data-testid="documents-locked">
                    {p.status === 'REJECTED'
                      ? 'No invoice or receipt — payment rejected.'
                      : 'Invoice and receipt: Available after Finance approval.'}
                  </Typography>
                )}
                {documentsAvailable(p) && (
                  <>
                <Button
                  size="small"
                  startIcon={<DescriptionIcon />}
                  onClick={() =>
                    setPreviewTarget({
                      key: `invoice-${p.id}`,
                      originalName: `Invoice ${p.paymentNumber}.pdf`,
                      mimeType: 'application/pdf',
                      fetchBlob: () => getInvoiceBlob(p.id),
                      onDownload: () => downloadInvoice(p.id, p.paymentNumber),
                    })
                  }
                >
                  Invoice
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadInvoice(p.id, p.paymentNumber)}
                >
                  Download
                </Button>
                  </>
                )}
                {p.receipt && canViewReceipts && (
                  <>
                    <Button
                      size="small"
                      startIcon={<ReceiptIcon />}
                      onClick={() =>
                        setPreviewTarget({
                          key: `receipt-${p.id}`,
                          originalName: `Receipt ${p.receipt!.receiptNumber}.pdf`,
                          mimeType: 'application/pdf',
                          fetchBlob: () => getReceiptBlob(p.id),
                          onDownload: () => downloadReceipt(p.id, p.receipt!.receiptNumber),
                        })
                      }
                    >
                      Receipt
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadReceipt(p.id, p.receipt!.receiptNumber)}
                    >
                      Download
                    </Button>
                  </>
                )}
                {p.receipt && canGenerateReceipt && (
                  <Button size="small" startIcon={<NoteAddIcon />} onClick={() => setReceiptTarget(p)}>
                    Generate Receipt
                  </Button>
                )}
                {/* Phase 107 — after recording, a receipt is added by Finance, or
                    by the recorder only when Finance has requested one. */}
                {p.status === 'PENDING' && canUploadSlip && (canApprove || !!p.evidenceRequestedAt) && (
                  <Button
                    size="small"
                    startIcon={<UploadFileIcon />}
                    disabled={slipUploadMutation.isPending}
                    onClick={() => {
                      slipUploadMutation.reset();
                      setSlipUploadTargetId(p.id);
                      slipInputRef.current?.click();
                    }}
                  >
                    {canApprove ? 'Upload Slip' : 'Upload requested receipt'}
                  </Button>
                )}
                {p.status === 'PENDING' && canApprove && (
                  <Button size="small" startIcon={<AttachFileIcon />} onClick={() => setRequestTarget(p)}>
                    Request receipt
                  </Button>
                )}
                {p.status === 'REJECTED' && !p.resubmission && canResubmit && (
                  <Button size="small" startIcon={<ReplayIcon />} onClick={() => openResubmit(p)}>
                    Correct &amp; resubmit
                  </Button>
                )}
                {p.status === 'PENDING' && canReview(p) && (
                  <>
                    {/* Confirmation is intentionally independent of whether a
                        slip was uploaded — see the "Payment Slip" caption
                        above; ticking Approve here is the one authorized
                        confirmation action, with or without evidence. */}
                    <Button
                      size="small"
                      color="success"
                      disabled={approveMutation.isPending}
                      onClick={() => approveMutation.mutate(p.id)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        rejectMutation.reset();
                        setRejectTarget(p);
                      }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
        {paymentsQuery.data?.data.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No payments recorded yet.
          </Typography>
        )}
      </Stack>

      <Dialog open={createOpen} onClose={resetForm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {resubmitting ? `Correct & resubmit ${resubmitting.paymentNumber}` : 'Record a payment'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {resubmitting && (
              <Alert severity="warning">
                Rejected: {resubmitting.rejectionReason ?? 'no reason recorded'}. Correct the
                details below — a new payment is submitted and {resubmitting.paymentNumber} stays on
                record as rejected.
              </Alert>
            )}
            <TextField
              select
              label="Type"
              fullWidth
              value={type}
              disabled={!!resubmitting}
              onChange={(e) => setType(e.target.value as PaymentType)}
            >
              {PAYMENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Category"
              fullWidth
              value={categoryId}
              onChange={(e) => setCategoryId(Number(e.target.value))}
            >
              {categoryOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            {categoryOptions.find((c) => c.id === categoryId)?.isTaxable === true && (
              <Alert severity="info">
                SSCL will be calculated and added automatically on top of the amount entered below.
              </Alert>
            )}
            <Stack direction="row" spacing={2}>
              <TextField label="Amount" type="number" fullWidth value={amount} onChange={(e) => setAmount(e.target.value)} />
              <TextField
                select
                label="Currency"
                fullWidth
                value={currencyId}
                onChange={(e) => setCurrencyId(Number(e.target.value))}
              >
                {currencyOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.code}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <ExchangeRateSelect value={paymentRate} onChange={setPaymentRate} canChoose={canChooseRate} />
            <TextField
              select
              label="Payment method"
              fullWidth
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Reference number"
              fullWidth
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
            <ReceiptFilePicker
              file={receiptFile}
              onChange={setReceiptFile}
              error={receiptError}
              onError={setReceiptError}
              progress={uploadProgress}
              disabled={createMutation.isPending}
              label={resubmitting ? 'Upload a corrected receipt / bank slip' : 'Upload Payment Receipt / Bank Slip'}
            />
            {resubmitting && !receiptFile && resubmitting.slipOriginalName && (
              <Typography variant="caption" color="text.secondary">
                Without a new file, a copy of {resubmitting.slipOriginalName} is attached.
              </Typography>
            )}
            <Alert severity="info">
              The payment is saved as <strong>Pending finance approval</strong>. It isn't counted as
              paid, and no receipt is issued, until Finance approves it.
            </Alert>
            {createMutation.isError && (
              <Alert severity="error">
                {errorMessage(createMutation.error, "Couldn't save the payment")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!categoryId || !amount || !currencyId || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending
              ? 'Saving…'
              : resubmitting
                ? 'Resubmit for approval'
                : 'Submit for approval'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject {rejectTarget?.paymentNumber}</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection reason"
            helperText="Required — the person who recorded it sees this."
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 500 }}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          {rejectMutation.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errorMessage(rejectMutation.error, "Couldn't reject the payment")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() =>
              rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason: rejectReason.trim() })
            }
          >
            {rejectMutation.isPending ? 'Rejecting…' : 'Reject payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <StudentLedgerDialog studentId={ledgerOpen ? studentId : null} onClose={() => setLedgerOpen(false)} />

      <RequestReceiptDialog
        payment={requestTarget}
        onClose={() => setRequestTarget(null)}
        onDone={invalidate}
      />

      <GenerateReceiptDialog
        payment={receiptTarget}
        canChooseRate={canChooseRate}
        onClose={() => setReceiptTarget(null)}
        onGenerated={(p, d) => {
          invalidate();
          previewReceiptDocument(p, d);
        }}
      />

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Box>
  );
};
